import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import { isArray } from 'lodash';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { environment } from '@app/env-service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class ChannelService {
  url :string = environment.url;

  channelType :any = {
    THIRDPARTY: 'third-party',
    CLIENT_PURCHASE_ORDER: 'client-purchase-order',
  };

  curPipe :CurrencyPipe;
  datePipe :DatePipe;

  storeTypes :string[] = ['', 'shopify', 'woocommerce', 'amazon'];
  storeProductStatus :any[] = [
    {},
    {
      all: 'All',
      active: 'Active',
      draft: 'Draft',
      archived: 'Archived',
    },
    {
      all: 'All',
      publish: 'Publish',
    },
  ];

  feeTypes :any[] = [
    { id: 1, type: 'Fixed Amount' },
    { id: 2, type: '% of SRP' },
    { id: 5, type: '% of Material Cost' },
  ];

  private readonly _channels = new BehaviorSubject<any>(<any>[]);
  public channels$ = this._channels.asObservable();

  public gettingRecords :boolean = false;

  constructor(
    private translate :TranslateService,
    private http :HttpClient,
  ) {
    this.curPipe = new CurrencyPipe('en');
    this.datePipe = new DatePipe('en');
  }

  getType(id :number) {
    return this.feeTypes.find((row) => row.id == id);
  }

  syncProducts(channelId :number, isNewProduct ?:boolean) :Observable<any> {
    return this.http.post(`${this.url}/channels/${channelId}/sync_products`, {
      isNewProduct: isNewProduct,
    });
  }
  pushProductInvToStore(channelId) {
    return this.http.post(
      `${this.url}/channels/${channelId}/push_products_stock`,
      {},
    );
  }
  renewQuickbooksToken(channelId) {
    return this.http.get(`${this.url}/channels/${channelId}/quickbooks`, {});
  }

  getHubSpotUrl(channelId) :Observable<any> {
    return this.http.get(`${this.url}/channels/${channelId}/hubspot/authorize`);
  }

  getHubSpotDeal(dealId) :Observable<any> {
    return this.http.get(`${this.url}/channels/hubspot/deal/${dealId}`);
  }

  syncHubSpotData(channelId, syncSettings) :Observable<any> {
    return this.http.post(
      `${this.url}/channels/${channelId}/hubspot/sync`,
      syncSettings,
    );
  }

  getEcartApiUrl(channelId) :Observable<any> {
    return this.http.get(`${this.url}/channels/${channelId}/ecartapi/authorize`);
  }

  get(params) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/channels`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        let responsData :any = {};
        if (params.all && !resp.data && isArray(resp)) {
          responsData.data = resp;
        } else {
          responsData = resp;
        }
        if (responsData && responsData.data) {
          responsData.data.forEach((row) => {
            if (row.fees instanceof String) {
              row.fees = JSON.parse(row.fees);
            }
          });
        }
        this._channels.next(responsData);
      });
  }

  getById(channelId :number) :Observable<any> {
    return this.http.get(`${this.url}/channels/${channelId}`);
  }

  updateChannel(channelId :number, orderData :any) :Observable<any> {
    return this.http.post(`${this.url}/channels/${channelId}`, orderData).pipe(
      tap((resp :any) => {
        this.tryUpdateOrAddResp(resp);
      }),
    );
  }

  updateChannelStatus(channelId :number, status :number) :Observable<any> {
    return this.http
      .put(`${this.url}/channels/${channelId}`, { status: status })
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  createChannel(orderData :any) :Observable<any> {
    return this.http.post(`${this.url}/channels`, orderData).pipe(
      tap((resp :any) => {
        setTimeout(() => {
          const channels :any = this._channels.getValue();
          if (channels && channels.data) {
            if (resp && resp.fees) {
              resp.fees = JSON.parse(resp.fees);
            }
            channels.data.splice(0, 0, resp);
            this._channels.next(channels);
          }
        });
      }),
    );
  }

  deleteChannel(channelId :number) {
    return this.http.delete(`${this.url}/channels/${channelId}`).pipe(
      tap((resp :any) => {
        const channels :any = this._channels.getValue();
        const poIdx = channels.data.findIndex((row) => row.id == channelId);
        channels.data.splice(poIdx, 1);
        this._channels.next(channels);
      }),
    );
  }

  tryUpdateOrAddResp(resp) {
    if (resp && resp.fees && resp.fees instanceof String) {
      resp.fees = JSON.parse(resp.fees);
    }
    const channels :any = this._channels.getValue();
    if (!channels || !channels.data) {
      return;
    }
    const poIdx = channels.data.findIndex((row) => row.id == resp.id);
    if (poIdx >= 0) {
      channels.data[poIdx] = resp;
    } else {
      channels.data.splice(0, 0, resp);
    }
    this._channels.next(channels);
  }

  checkShipstationStoreUsedInChannel(shipstaionStoreId :number) :Observable<any> {
    return this.http.get(
      `${this.url}/channels/isShipstationStoreUsedInChannel/${shipstaionStoreId}`,
    );
  }

  getChannelById(
    channelId :number,
    params :Record<string, any> = {},
  ) :Observable<any> {
    return this.http.get(`${this.url}/channels/${channelId}`, {
      params,
    });
  }
}
